import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, TreePalm, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate, formatNumber, relativeTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

export interface LeaveBalance {
  leaveType: { id: string; code: string; name: string; nameAr: string | null; isPaid: boolean };
  year: number;
  /** The year's total: the type's allowance and any days added beside it. */
  allowance: number | null;
  /** The type's own yearly allowance. */
  annualAllowance?: number | null;
  /** Days added beside the allowance — bought with wallet points (CR-WALLET-001). */
  grantedDays?: number;
  /** Days taken from the year instead of salary for days more than an hour short (CR-004); off `remaining`. */
  deductedDays?: number;
  approvedDays: number;
  pendingDays: number;
  remaining: number | null;
}

type Scope = 'mine' | 'review' | 'all';

/** Route: `/leaves` — my requests; and for whoever decides leave, the queue and everything. A row opens its request. */
export function LeavesPage() {
  const { t } = useI18n();
  const { can, ledDepartmentIds } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // A department leader decides their department's requests; leaves.approve decides anyone's.
  const reviewer = can(P.LEAVES_APPROVE) || ledDepartmentIds.length > 0;
  const requested = params.get('tab') as Scope | null;
  const tab: Scope = reviewer && (requested === 'review' || requested === 'all') ? requested : 'mine';

  const pending = useQuery({
    queryKey: ['leaves', 'review', 'count'],
    queryFn: () => api.get<Paginated<unknown>>('/leaves', { scope: 'review', status: 'PENDING', pageSize: 1 }),
    enabled: reviewer,
  });

  return (
    <>
      <PageHeader
        title={t('leaves.title')}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/leaves/new')}>
            {t('leaves.new')}
          </Button>
        }
      />
      {reviewer && (
        <div className="mb-5">
          <Tabs
            active={tab}
            onChange={(id) => setParams(id === 'mine' ? {} : { tab: id }, { replace: true })}
            tabs={[
              { id: 'mine', label: t('leaves.mine') },
              { id: 'review', label: t('leaves.review'), count: pending.data?.total || undefined },
              { id: 'all', label: t('leaves.all') },
            ]}
          />
        </div>
      )}
      {tab === 'mine' && <Balances />}
      <LeaveList key={tab} scope={tab} />
    </>
  );
}

function Balances() {
  const { t, locale, localized } = useI18n();
  const year = new Date().getFullYear();
  const { data } = useQuery({
    queryKey: ['leaves', 'balance', year],
    queryFn: () => api.get<LeaveBalance[]>('/leaves/balance', { year }),
  });
  const withAllowance = (data ?? []).filter((b) => b.allowance != null);
  if (!withAllowance.length) return null;

  return (
    <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {withAllowance.map((b) => (
        <div key={b.leaveType.id} className="surface rounded-lg p-4">
          <div className="flex items-start justify-between gap-2">
            <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{localized(b.leaveType)}</span>
            <span className="text-caption text-faint tabular">{t('leaves.balance', { year: String(b.year) })}</span>
          </div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <span className="text-metric tabular text-[var(--text-primary)]">{formatNumber(b.remaining, locale)}</span>
            <span className="text-body-sm text-muted tabular">/ {formatNumber(b.allowance, locale)}</span>
          </div>
          <p className="mt-1 text-caption text-muted">
            {t('leaves.used')} {formatNumber(b.approvedDays, locale)}
            {b.pendingDays > 0 && ` · ${t('leaves.pending')} ${formatNumber(b.pendingDays, locale)}`}
            {!!b.grantedDays && ` · ${t('leaves.grantedDays', { n: formatNumber(b.grantedDays, locale) })}`}
            {!!b.deductedDays && ` · ${t('leaves.deductedDays', { n: formatNumber(b.deductedDays, locale) })}`}
          </p>
        </div>
      ))}
    </div>
  );
}

function LeaveList({ scope }: { scope: Scope }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(scope === 'review' ? 'PENDING' : '');
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leaves', 'list', scope, page, status, q],
    queryFn: () =>
      api.get<Paginated<any>>('/leaves', { scope, page, pageSize: 25, status: status || undefined, q: q || undefined }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['leaves'] });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/leaves/${id}/approve`, {}),
    onSuccess: () => {
      toast.success(t('leaves.approved'));
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/leaves/${id}/cancel`),
    onSuccess: () => {
      toast.success(t('leaves.cancelled'));
      refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const showEmployee = scope !== 'mine';

  return (
    <>
      {scope !== 'mine' && (
        <Card className="mb-4" padded={false}>
          <div className="grid gap-3 p-4 sm:grid-cols-3">
            <Input placeholder={t('common.search')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('common.status')}>
              <option value="">{t('common.status')} — {t('common.all')}</option>
              {['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].map((s) => (
                <option key={s} value={s}>{enumLabel(s, locale)}</option>
              ))}
            </Select>
          </div>
        </Card>
      )}

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<TreePalm className="h-8 w-8" />}
            title={scope === 'review' ? t('leaves.nothingToReview') : t('leaves.none')}
            hint={scope === 'mine' ? t('leaves.noneHint') : ''}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  {showEmployee && <Th>{t('common.employee')}</Th>}
                  <Th>{t('leaves.type')}</Th>
                  <Th>{t('leaves.dates')}</Th>
                  <Th align="end">{t('leaves.days')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.reason')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <Tr key={r.id} onClick={() => navigate(`/leaves/${r.id}`)}>
                    {showEmployee && (
                      <Td>
                        <span className="block">{localized(r.user)}</span>
                        {r.department && <span className="text-caption text-faint">{localized(r.department)}</span>}
                      </Td>
                    )}
                    <Td>
                      <span className="block">{localized(r.leaveType)}</span>
                      {!r.leaveType.isPaid && <span className="text-caption text-faint">{t('leaves.unpaid')}</span>}
                    </Td>
                    <Td className="tabular text-muted">
                      {formatDate(r.startDate, locale)}
                      {r.endDate !== r.startDate && <> – {formatDate(r.endDate, locale)}</>}
                    </Td>
                    <Td align="end" className="tabular">{formatNumber(r.days, locale)}</Td>
                    <Td>
                      <Badge tone={statusTone(r.status)}>{enumLabel(r.status, locale)}</Badge>
                      {r.reviewedBy && (
                        <span className="mt-0.5 block text-caption text-faint" title={r.reviewNote ?? undefined}>
                          {t('leaves.reviewedBy')}: {localized(r.reviewedBy)}
                        </span>
                      )}
                    </Td>
                    <Td className="max-w-xs">
                      <span className="block truncate text-muted" title={r.reason ?? undefined}>{r.reason || '—'}</span>
                      {r.reviewNote && (
                        <span className="block truncate text-caption text-faint" title={r.reviewNote}>“{r.reviewNote}”</span>
                      )}
                      {scope === 'review' && (
                        <span className="block text-caption text-faint">{relativeTime(r.createdAt, locale)}</span>
                      )}
                    </Td>
                    <Td align="end">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {r.can.decide && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<Check className="h-3.5 w-3.5" />}
                              disabled={approve.isPending}
                              onClick={() => approve.mutate(r.id)}
                            >
                              {t('common.approve')}
                            </Button>
                            <Button size="sm" variant="ghost" icon={<X className="h-3.5 w-3.5" />} onClick={() => navigate(`/leaves/${r.id}/reject`)}>
                              {t('common.reject')}
                            </Button>
                          </>
                        )}
                        {r.can.cancel && (
                          <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id)}>
                            {t('leaves.cancelRequest')}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
